import { Box, Typography } from '@mui/material';
import { osirisTokens } from '@/theme/tokens';
import authBgImage from '@/assets/auth_bg.avif';
import viewfinderSvg from '@/assets/vector.svg';

interface AuthShellProps {
  children: React.ReactNode;
}

const APP_VERSION = '0.0.1';

function HeroViewfinder() {
  // SVG already encodes the cross/angle (~12°). Rotate the whole block so labels
  // tilt with the cross, and keep the gaps tight.
  const labelSx = {
    fontFamily: osirisTokens.fonts.mono,
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  } as const;

  return (
    <Box
      className="auth-hero-viewfinder"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-12deg)',
        display: 'grid',
        gridTemplateColumns: 'auto 81px auto',
        gridTemplateRows: 'auto 81px auto',
        columnGap: 0,
        rowGap: 0,
        alignItems: 'center',
        justifyItems: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Row 1: ALT (top-left)  ·  empty  ·  SPD (top-right) — pulled down toward the cross */}
      <Typography sx={{ ...labelSx, justifySelf: 'end', mb: '-30px' }}>ALT 124m</Typography>
      <Box />
      <Typography sx={{ ...labelSx, justifySelf: 'start', mb: '-30px' }}>SPD 12.4</Typography>

      {/* Row 2: empty  ·  viewfinder SVG  ·  empty */}
      <Box />
      <Box
        component="img"
        src={viewfinderSvg}
        alt=""
        sx={{
          width: 81,
          height: 81,
          display: 'block',
          // SVG itself bakes in the cross angle for an unrotated reticle.
          // Counter the outer rotation so the cross renders in the design's angle.
          transform: 'rotate(12deg)',
        }}
      />
      <Box />

      {/* Row 3: DG (bottom-left)  ·  empty  ·  BAT (bottom-right) — pulled up toward the cross */}
      <Typography sx={{ ...labelSx, justifySelf: 'end', mt: '-30px' }}>DG 274°</Typography>
      <Box />
      <Typography sx={{ ...labelSx, justifySelf: 'start', mt: '-30px' }}>BAT 87%</Typography>
    </Box>
  );
}

function HeroLogo() {
  return (
    <Box
      className="auth-hero-logo"
      sx={{
        position: 'absolute',
        // 40px inset from the hero corner (Figma Auth - Sign In 28408:19502)
        bottom: 40,
        left: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* Logo-Osiris vector — logotype and tagline are baked into the one asset */}
      <Box
        component="img"
        src="/osiris-logo-full.svg"
        alt="OSIRIS AI — Drone Fleet Management"
        sx={{ height: 48 }}
      />

      <Box
        sx={{
          width: '1px',
          height: 36,
          bgcolor: 'rgba(255,255,255,0.20)',
          alignSelf: 'center',
        }}
      />

      <Box>
        <Typography
          sx={{
            fontFamily: osirisTokens.fonts.mono,
            fontSize: 11,
            letterSpacing: '0.08em',
            color: 'rgba(255,255,255,0.75)',
            textTransform: 'uppercase',
            lineHeight: 1.4,
          }}
        >
          SYS:{' '}
          <Box component="span" sx={{ color: osirisTokens.accent.mid }}>
            ACTIVE
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: osirisTokens.fonts.mono,
            fontSize: 11,
            letterSpacing: '0.08em',
            color: 'rgba(255,255,255,0.75)',
            textTransform: 'uppercase',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
          }}
        >
          VER {APP_VERSION} [Encrypted]
        </Typography>
      </Box>
    </Box>
  );
}

export function AuthShell({ children }: AuthShellProps) {
  return (
    <Box
      className="auth-shell"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: osirisTokens.bg.page,
        color: osirisTokens.text.primary,
      }}
    >
      {/* Left: hero with aerial terrain background */}
      <Box
        className="auth-hero"
        sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          display: { xs: 'none', md: 'block' },
          overflow: 'hidden',
          bgcolor: '#0a0a0d',
          backgroundImage: `url(${authBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Subtle dark overlay — kept light, the hero read too dark vs the
            mock (Figma To Do 28693:28012) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(10, 10, 13, 0.1)',
          }}
        />

        <HeroViewfinder />
        <HeroLogo />
      </Box>

      {/* Right: form column */}
      <Box
        className="auth-form-column"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: osirisTokens.bg.page,
          borderLeft: { md: `1px solid ${osirisTokens.border.primary}` },
          px: { xs: 3, md: 6 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>{children}</Box>
      </Box>
    </Box>
  );
}
